/**
 * Roles & Access, inside a workspace.
 *
 * The same screens the platform console renders, with two things different and
 * nothing else: the tenant is fixed to the caller's own workspace (or one
 * beneath it, for a supertenant), and the platform-wide tabs — the grant matrix,
 * platform staff, the global audit log — are not offered.
 *
 * Deliberately a wrapper rather than a second implementation. A tenant admin
 * editing a role has to go through the same code path a platform operator does,
 * or the two consoles disagree about what a role means. The server draws the
 * real boundary either way: routes_access_control._require_access_admin scopes
 * every read and write to `accessible_tenant_ids`, and refuses any role that
 * ranks at or above the caller's own.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import SuperadminAccessControlPage from '../pages/SuperadminAccessControlPage';

const TABS = [
  { id: 'roles', label: 'Roles & access' },
  { id: 'user-permissions', label: 'User overrides' },
  { id: 'simulator', label: 'Simulator' },
];

// Mirrors ROLE_METADATA on the server. Only used to grey out an edit the server
// is going to refuse anyway — routes_access_control._require_role_below_caller
// is the one that decides, and a custom role ranks below every built-in admin.
const ROLE_RANK = {
  superadmin: 100, workspace_admin: 90, hr_admin: 80,
  manager: 70, hr_manager: 60, employee: 10,
};
const rankOf = (role) => ROLE_RANK[String(role || '').toLowerCase()] ?? 50;

const TenantAccessControl = () => {
  const { user } = useAuth();
  const [view, setView] = useState('roles');
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(user?.tenantId || null);

  useEffect(() => { setTenantId((prev) => prev || user?.tenantId || null); }, [user?.tenantId]);

  // A supertenant administers its children too, so it needs to say which one it
  // is editing. A plain tenant admin gets exactly one row back and no picker.
  useEffect(() => {
    let cancelled = false;
    api.get('/api/access-control/tenants')
      .then(({ data }) => { if (!cancelled) setTenants(data?.tenants || []); })
      .catch(() => { /* the picker is a convenience; the console still works */ });
    return () => { cancelled = true; };
  }, []);

  const showPicker = useMemo(() => tenants.length > 1, [tenants]);

  if (!tenantId) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-4">
      <header>
        <h2 className="text-xl font-semibold">Roles &amp; Access</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Who may do what in this workspace. You can edit roles below your own —
          your own role and anything above it are managed by your platform operator.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <nav className="flex gap-1 border-b border-border flex-1 min-w-0 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setView(t.id)}
              className={`px-3 py-2 text-sm rounded-t-lg -mb-px border-b-2 whitespace-nowrap ${
                view === t.id ? 'border-primary text-primary font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              {t.label}
            </button>
          ))}
        </nav>
        {showPicker && (
          <select
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            value={tenantId}
            onChange={(e) => setTenantId(Number(e.target.value))}
          >
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name || t.slug}</option>
            ))}
          </select>
        )}
      </div>

      <SuperadminAccessControlPage
        view={view}
        tenantId={tenantId}
        canEditRole={(role) => rankOf(role) < rankOf(user?.role)}
      />
    </div>
  );
};

export default TenantAccessControl;
